import moment, { Moment } from 'moment-timezone';

export const convertToUtc = (date: string | Moment) => {
  if (!date) return '';
  return moment(date).utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]');
};

export const utcDayStart = (date: string | Moment) => convertToUtc(moment.utc(date).startOf('day'));

export const utcDayEnd = (date: string | Moment) => convertToUtc(moment.utc(date).endOf('day'));

const today = new Date();

const findMonthDays = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const findFirstDay = (year: number, month: number) => {
  return new Date(year, month, 1).getDay();
};

const findLastDays = (period: number | string) => {
  let lastDay;
  let firstDay;

  if (typeof period === 'number') {
    lastDay = new Date(today);
    firstDay = new Date(today);
    if (period === 30) {
      firstDay = moment().subtract(29, 'days').toDate();
    } else {
      firstDay.setDate(today.getDate() - (period - 1));
    }
  } else if (period === 'month') {
    lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  } else {
    const currentDayOfWeek = today.getDay();
    firstDay = new Date(today);
    firstDay.setDate(today.getDate() - currentDayOfWeek - 6);
    lastDay = new Date(firstDay);
    lastDay.setDate(firstDay.getDate() + 6);
  }

  const firstMonth = new Intl.DateTimeFormat('en', { month: 'short' }).format(firstDay);
  const firstDayOfMonth = firstDay.getDate();
  const lastMonth = new Intl.DateTimeFormat('en', { month: 'short' }).format(lastDay);
  const lastDayOfMonth = lastDay.getDate();

  const dateRange = `${firstMonth} ${firstDayOfMonth} - ${lastMonth} ${lastDayOfMonth}`;

  return {
    dateRange,
    dates: [firstDay, lastDay],
  };
};

const isBetweenDates = (currDate: Date, dates: Date[]) => {
  if (dates.length === 1) {
    return false;
  }

  return (
    new Date(currDate.toDateString().slice(4)) > new Date(dates[0].toDateString().slice(4)) &&
    new Date(currDate.toDateString().slice(4)) < new Date(dates[1].toDateString().slice(4))
  );
};

const isSelected = (rangeDate: Date[], date: Date, range: boolean | undefined, clicked: Date | undefined) => {
  if (rangeDate.length === 2 && range) {
    return rangeDate.map(el => el.toDateString()).includes(date.toDateString());
  }
  return !clicked && rangeDate[0].toDateString().includes(date.toDateString());
};

const isClicked = (clicked: Date | undefined, date: Date) => {
  return clicked?.toDateString() === date.toDateString();
};

const isPicked = (pickedDate: Date | undefined, date: Date) => {
  return pickedDate?.toDateString() === date.toDateString();
};

const setCorrectTime = (date: string, time: string) => {
  const parsedDate = moment(date, 'DD/MM/YYYY');
  const isToday = parsedDate.isSame(moment(), 'day');
  if (!isToday) return time;

  const currentTime = moment();
  const minimumTime = currentTime.clone().add(1, 'hour');
  const parsedInputTime = moment(time, 'HH:mm');
  const isLater = parsedInputTime.isSameOrAfter(minimumTime, 'minute');
  const correctTime = isLater ? time : minimumTime.format('HH:mm');
  return correctTime;
};

const normalizeTime = (time: string) => {
  const parsedTime = moment(time, 'HH:mm');
  const formattedTime = parsedTime.format('h:mm A');
  return formattedTime;
};

const formattedDate = (date: string, lang: string) => {
  return new Intl.DateTimeFormat(lang, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(date));
};

const getTimeZone = (date?: string) => {
  const zone = moment.tz.guess();
  const formattedTimeZone = moment.tz(date ?? new Date(), zone).format('Z');

  return formattedTimeZone;
};

const yearDateRange = (item: any) => {
  const today = moment(item.startDate);
  const target = moment(item.endDate);
  const differenceInDays = target.diff(today, 'days');

  return differenceInDays > 31;
};

const formatInputDate = (value: string) => {
  let formattedValue = value.replace(/[.]/g, '');
  if (formattedValue.length >= 5) {
    formattedValue = `${formattedValue.slice(0, 2)}.${formattedValue.slice(2, 4)}.${formattedValue.slice(4)}`;
  } else if (formattedValue.length > 2 && formattedValue.length < 4) {
    formattedValue = `${formattedValue.slice(0, 2)}.${formattedValue.slice(2)}`;
  } else {
    formattedValue = value;
  }

  return formattedValue;
};

export const formatTime = (isoDate: string) => {
  const date = new Date(isoDate);
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return formattedTime;
};

export {
  findMonthDays,
  findFirstDay,
  findLastDays,
  isBetweenDates,
  isSelected,
  isClicked,
  isPicked,
  formattedDate,
  getTimeZone,
  setCorrectTime,
  normalizeTime,
  yearDateRange,
  formatInputDate,
};
